import { useState, useEffect, useRef } from 'react';
import { ChevronDown, AlertTriangle, Loader } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// ── Game key mapping (matches Firestore document keys) ──
const GAME_KEY_MAP: Record<string, string> = {
    'PUBG Mobile': 'pubg',
    'PUBG': 'pubg',
    'pubg': 'pubg',
    'Free Fire': 'freefire',
    'freefire': 'freefire',
    'Wild Rift': 'wildrift',
    'wildrift': 'wildrift',
    'MLBB': 'mlbb',
    'mlbb': 'mlbb',
    'Mobile Legends': 'mlbb',
};

// ── Fallback mock rules (used when Firestore has no data) ──
const MOCK_GAME_RULES: Record<string, string[]> = {
    pubg: [
        "All matches will be played on PUBG Mobile (Classic Mode)",
        "Game mode (Solo/Duo/Squad) and map will be announced before the match",
        "Players must join the custom room on time — late entries may not be allowed",
        "No changes in team lineup after the tournament starts",
        "Use of hacks, cheats, or unfair tools will result in immediate disqualification",
        "Teaming with opponents in Solo or Duo mode is strictly prohibited",
        "Respect all players and organizers — toxic behavior will not be tolerated",
        "Points will be awarded based on placement and kills",
        "Decisions made by the organizers will be final",
        "In case of a tie, ranking will be decided by total kills then best match performance",
        "Room ID and password will be shared shortly before each match",
        "Players must ensure a stable internet connection — disconnects are their own responsibility",
    ],
    freefire: [
        "All matches will be played on Free Fire (Battle Royale Mode)",
        "Game mode (Solo/Duo/Squad) and map will be announced before the match",
        "Players must join the custom room on time — late entries may not be allowed",
        "No changes in team lineup after the tournament starts",
        "Use of hacks, cheats, or any unauthorized tools will result in immediate disqualification",
        "Teaming with opponents in Solo or Duo mode is strictly prohibited",
        "Respect all players and organizers — toxic behavior will not be tolerated",
        "Points will be awarded based on placement and kills",
        "Decisions made by the organizers will be final",
        "In case of a tie, ranking will be decided by total kills then best match performance",
        "Room ID and password will be shared shortly before each match",
        "Players must ensure a stable internet connection — disconnects are their own responsibility",
        "Only mobile devices are permitted — emulators are strictly banned",
    ],
    wildrift: [
        "Intentional feeding or AFK behavior results in immediate disqualification",
        "Players must use their registered in-game name during tournament matches",
        "Champion select is final — dodging results in a match loss",
        "No communication with opposing team during active matches",
        "All matches must be played on the official tournament server",
    ],
    mlbb: [
        "Players must be present in the lobby 5 minutes before match start time",
        "Intentional disconnecting to avoid a loss results in disqualification",
        "Hero bans and picks are final once confirmed",
        "No use of hacks, scripts, or unauthorized game modifications",
        "Disputes must be raised with the referee before the next match begins",
    ],
};

interface GameRulesCardProps {
    game: string;
}

export default function GameRulesCard({ game }: GameRulesCardProps) {
    const [rules, setRules] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState(0);

    const gameKey = GAME_KEY_MAP[game] || game?.toLowerCase?.() || '';

    // Fetch rules from Firestore, fallback to mock data
    useEffect(() => {
        if (!gameKey) {
            setLoading(false);
            return;
        }

        const fetchRules = async () => {
            try {
                const snap = await getDoc(doc(db, 'platformConfig', 'gameRules'));
                if (snap.exists()) {
                    const data = snap.data();
                    const fetched = data[gameKey]?.rules || [];
                    if (fetched.length > 0) {
                        setRules(fetched);
                    } else {
                        // Firestore doc exists but no rules for this game — use fallback
                        setRules(MOCK_GAME_RULES[gameKey] || []);
                    }
                } else {
                    // No Firestore document at all — use fallback mock rules
                    setRules(MOCK_GAME_RULES[gameKey] || []);
                }
            } catch (err) {
                console.error('Failed to load game rules:', err);
                // Fetch failed — use fallback mock rules
                setRules(MOCK_GAME_RULES[gameKey] || []);
            } finally {
                setLoading(false);
            }
        };
        fetchRules();
    }, [gameKey]);

    // Measure content height for smooth animation
    useEffect(() => {
        if (contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight);
        }
    }, [rules, expanded]);

    // Don't render if no rules or loading
    if (loading) {
        return (
            <div className="rules-card">
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)' }}>
                    <Loader size={18} className="spin" />
                </div>
            </div>
        );
    }

    if (rules.length === 0) return null;

    const COLLAPSED_COUNT = 3;
    const needsExpansion = rules.length > COLLAPSED_COUNT;
    const visibleRules = expanded ? rules : rules.slice(0, COLLAPSED_COUNT);

    return (
        <div className="rules-card animate-in">
            {/* Header */}
            <div
                className="rules-header"
                onClick={() => needsExpansion && setExpanded(!expanded)}
                style={{ cursor: needsExpansion ? 'pointer' : 'default' }}
            >
                <div className="rules-header-left">
                    <span className="rules-icon">📋</span>
                    <h3 className="rules-title">Game Rules</h3>
                    <span className="rules-count">{rules.length}</span>
                </div>
                {needsExpansion && (
                    <div className={`rules-chevron ${expanded ? 'rules-chevron-expanded' : ''}`}>
                        <ChevronDown size={18} />
                    </div>
                )}
            </div>

            {/* Rules List */}
            <div
                className="rules-content-wrapper"
                style={{
                    maxHeight: expanded ? contentHeight + 40 : COLLAPSED_COUNT * 48 + 8,
                    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                <div ref={contentRef} className="rules-list">
                    {visibleRules.map((rule, idx) => (
                        <div key={idx} className="rules-item">
                            <div className="rules-item-number">{idx + 1}</div>
                            <AlertTriangle size={13} className="rules-item-icon" />
                            <span className="rules-item-text">{rule}</span>
                        </div>
                    ))}
                    {!expanded && rules.length > COLLAPSED_COUNT && (
                        <div className="rules-remaining">
                            {rules.slice(COLLAPSED_COUNT).map((rule, idx) => (
                                <div key={idx + COLLAPSED_COUNT} className="rules-item">
                                    <div className="rules-item-number">{idx + COLLAPSED_COUNT + 1}</div>
                                    <AlertTriangle size={13} className="rules-item-icon" />
                                    <span className="rules-item-text">{rule}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Fade gradient when collapsed */}
                {!expanded && needsExpansion && (
                    <div className="rules-fade-gradient" />
                )}
            </div>

            {/* Expand/Collapse Button */}
            {needsExpansion && (
                <button
                    className="rules-toggle-btn"
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? 'Show Less' : `Show All ${rules.length} Rules`}
                    <ChevronDown
                        size={14}
                        style={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                        }}
                    />
                </button>
            )}
        </div>
    );
}
